import { sessionKeys } from "@features/history/hooks/useSessions";
import { useChatStore } from "@stores/chatStore";
import type { QueryClient } from "@tanstack/react-query";
import { recoverFromStreamDrop } from "./chatRecovery";

/**
 * Status-aware reattach for a hydrated chat session.
 *
 * Asks the backend whether the session has a still-running agent run
 * (`/chat/sessions/:id/run-status`); if so, resumes the live SSE stream
 * via `recoverFromStreamDrop`. Cheap one-GET no-op when the run is
 * terminal — safe to call after every session hydration.
 *
 * Two entry points share this helper so a refresh and a sidebar click
 * behave identically:
 *
 *   - `loadSessionById` (sidebar click → useChat) — calls it after the
 *     page fetch so the user sees live activity if they switched into
 *     a session whose run is still in flight.
 *   - `ChatPage` URL hydration (refresh / deep-link) — calls it after
 *     its own page fetch so a hard refresh during a long agent turn
 *     keeps streaming instead of silently waiting for `done`.
 *
 * The stop signal is module-level on purpose. Both entry points may
 * fire in quick succession (e.g. URL hydration finishes just as the
 * user clicks a different session); a per-caller signal would let
 * the late finisher race the new owner. The single signal guarantees
 * exactly one reattach is alive at any moment, regardless of caller.
 */

let stopSignal: { current: boolean } | null = null;

/**
 * Cancel any in-flight reattach. Idempotent — safe to call from
 * unmount cleanups, "new chat" handlers, and anywhere else the
 * caller wants to walk away from the active session.
 */
export function cancelReattach(): void {
  if (stopSignal) stopSignal.current = true;
  stopSignal = null;
}

export async function reattachToSession(
  sessionId: string,
  qc: QueryClient,
): Promise<void> {
  // Tear down any prior in-flight reattach (rapid A→B→A switching, or
  // refresh racing a sidebar click) before claiming the signal.
  if (stopSignal) stopSignal.current = true;
  const myStop = { current: false };
  stopSignal = myStop;

  // Pull store actions once at entry. Zustand actions are stable
  // references, so capturing them up front (instead of touching
  // getState() inside each guard) keeps the closure tight.
  const {
    setStreaming,
    setReconnecting,
    setMessages,
    pushActivity,
    pushLiveArtifact,
  } = useChatStore.getState();

  // Watchdog — if the recovery flow neither pushes any activity nor
  // returns within this window, abort it and clear the banner. The
  // backend's 1 h reattach timeout is the right ceiling for a live
  // run that's actively producing output, but for the refresh path
  // we'd rather give up quickly on an orphaned-running run row than
  // leave the user staring at "Reconnecting…" indefinitely.
  const WATCHDOG_MS = 15_000;
  const watchdog = setTimeout(() => {
    if (myStop.current) return;
    console.warn("[chat] reattach watchdog: no activity within 15s, giving up");
    myStop.current = true;
    // Best-effort cleanup — the recovery promise will see myStop on
    // its next tick and bail; we clear flags here so the banner hides
    // immediately rather than waiting for the SSE fetch to abort.
    setStreaming(false);
    setReconnecting(false);
  }, WATCHDOG_MS);

  try {
    setStreaming(true);
    // Drives the thin "Reconnecting to the active run…" banner in
    // ChatContainer. Cleared automatically once real activity flows
    // (the banner's render condition checks `activity.length === 0`)
    // and explicitly in the finally block on completion.
    setReconnecting(true);

    let firstActivitySeen = false;
    const onFirstActivity = () => {
      if (firstActivitySeen) return;
      firstActivitySeen = true;
      // Real events are flowing — the user is no longer "reconnecting",
      // they're watching the run continue. Cancel the watchdog and
      // clear the reconnect flag so the banner hides crisply (don't
      // wait for `done` or for the activity-empty render condition to
      // catch up on a re-render).
      clearTimeout(watchdog);
      setReconnecting(false);
    };

    const guard =
      <T>(fn: (x: T) => void) =>
      (x: T) => {
        if (myStop.current) return;
        onFirstActivity();
        fn(x);
      };
    const guard2 =
      <A, B>(fn: (a: A, b: B) => void) =>
      (a: A, b: B) => {
        if (myStop.current) return;
        fn(a, b);
      };

    if (import.meta.env.DEV) {
      console.debug("[chat] reattach: starting recovery", { sessionId });
    }
    const result = await recoverFromStreamDrop({
      sessionId,
      capturedRunId: null,
      setMessages: guard2(setMessages),
      pushActivity: guard(pushActivity),
      pushLiveArtifact: guard(pushLiveArtifact),
      stopSignal: myStop,
    });
    if (import.meta.env.DEV) {
      console.debug("[chat] reattach: recovery returned", {
        sessionId,
        result,
      });
    }
  } finally {
    clearTimeout(watchdog);
    // Only clear if WE'RE still the active reattach. If another caller
    // claimed the signal mid-flight they own the streaming/reconnecting
    // flags now; flipping them off here would step on their setup.
    if (!myStop.current) {
      setStreaming(false);
      setReconnecting(false);
      void qc.invalidateQueries({ queryKey: sessionKeys.all });
    }
    if (stopSignal === myStop) stopSignal = null;
  }
}
